import {
  Badge,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import {
  InquiryVersionSlot,
  InquiryWithContext,
  SupplierInquiry,
  TierPrice,
  inquiryApi,
} from '@texas/api/endpoints/inquiryApi';
import { CopyValue } from '@texas/components/shared/CopyValue';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';
import { CompositionGroupTitleWithHover } from '@texas/components/shared/compositionGroup/CompositionGroupTitleWithHover';
import { Icons } from '@texas/components/shared/Icons';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import React, { useState } from 'react';
import { SetTierPriceModal } from '../SetTierPriceModal';
import { useTranslation } from 'react-i18next';
import { inquiryEvents } from '../events';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { InquiryTierPriceHistoryModal } from '../InquiryTierPriceHistoryModal';
import { TierCell } from './TierCell';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import {
  isTierLocked,
  useInquiryTierPriceSelection,
} from '../useInquiryTierPriceSelection';
import { TiersEditDrawer } from '../TiersEditDrawer';
import { WarningComponent } from '@texas/components/shared/WarningComponent';

interface HistoryDialogValue {
  tierPriceId: number;
  currencyCode: string;
  title: string;
}

interface InquiryTableProps {
  masterInquiry: SupplierInquiry;
  inquiries: InquiryWithContext[];
  missingCellSlots: InquiryVersionSlot[];
  allTierColumns: number[];
  targetTierQuantities: Set<number>;
  hasAnyX: boolean;
  hasAnyY: boolean;
  toolbarControls?: React.ReactNode;
  onChanged?: () => void;
  rowActions?: (inq: InquiryWithContext) => React.ReactNode;
  hideIdentityColumns?: boolean;
}

export function InquiryTable({
  masterInquiry,
  inquiries,
  missingCellSlots,
  allTierColumns,
  targetTierQuantities,
  hasAnyX,
  hasAnyY,
  toolbarControls,
  onChanged,
  rowActions,
  hideIdentityColumns,
}: InquiryTableProps) {
  const { t } = useTranslation();
  const {
    useCurrenciesData: { findCurrency },
  } = useMetadata();

  const mainTierQuantities = new Set(
    masterInquiry.tierPrices
      .filter((tier) => tier.isMain)
      .map((tier) => tier.quantity),
  );
  const mainTier = masterInquiry.tierPrices.find((tier) => tier.isMain);

  const [modalOpen, setModalOpen] = useState(false);
  const [showWithFees, setShowWithFees] = useState(false);
  const [comparePreviousPrice, setComparePreviousPrice] = useState(false);
  const acceptDialog = useDisclosure();
  const rejectDialog = useDisclosure();
  const reviewDialog = useDisclosure();
  const newVersionDialog = useDisclosure();
  const historyDialog = useValueDisclosure<HistoryDialogValue>();
  const tierDrawer = useDisclosure();
  const { request: setMainTierRequest, loading: settingMainTier } =
    useApiRequest(inquiryApi.setMainTierPrice);
  const { request: createTierPriceVersion } = useApiRequest(
    inquiryApi.createTierPriceVersion,
  );
  const { request: acceptTierPrices } = useApiRequest(
    inquiryApi.acceptTierPrices,
  );
  const { request: rejectTierPrices } = useApiRequest(
    inquiryApi.rejectTierPrices,
  );
  const { request: reviewTierPrices } = useApiRequest(
    inquiryApi.reviewTierPrices,
  );
  const {
    selectedTierPriceIds,
    selectedAcceptableTierPriceIds,
    selectedLockedTierPriceIds,
    selectedReviewedTierPriceIds,
    selectedEditableTierPriceIds,
    selectableTierPriceIds,
    toggleCell,
    toggleRow,
    toggleColumn,
    selectAllPrices,
    clearSelection,
    buildSelectedCells,
  } = useInquiryTierPriceSelection(inquiries);

  async function setMainTier(tierId: number) {
    const response = await setMainTierRequest(masterInquiry.id, tierId);
    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
      onChanged?.();
    }
  }

  async function createSingleVersion(tierPriceId: number) {
    const response = await createTierPriceVersion(tierPriceId);
    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
      onChanged?.();
    }
  }

  async function acceptSingleTierPrice(tierPriceId: number) {
    const response = await acceptTierPrices({ tierPriceIds: [tierPriceId] });
    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
      onChanged?.();
    }
  }

  async function rejectSingleTierPrice(tierPriceId: number) {
    const response = await rejectTierPrices({ tierPriceIds: [tierPriceId] });
    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
      onChanged?.();
    }
  }

  async function reviewSingleTierPrice(tierPriceId: number) {
    const response = await reviewTierPrices({ tierPriceIds: [tierPriceId] });
    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
      onChanged?.();
    }
  }

  function openHistory(
    tierPriceId: number,
    currencyCode: string,
    inquiry: InquiryWithContext,
  ) {
    historyDialog.onOpen({
      tierPriceId,
      currencyCode,
      title: getInquiryHistoryTitle(inquiry),
    });
  }

  function getDisplayTierPrice(
    inquiry: InquiryWithContext,
    quantity: number,
  ): TierPrice | undefined {
    return inquiry.tierPrices
      .filter((tp) => tp.quantity === quantity)
      .sort((a, b) => {
        if (!isTierLocked(a) && isTierLocked(b)) return -1;
        if (isTierLocked(a) && !isTierLocked(b)) return 1;
        return b.id - a.id;
      })[0];
  }

  return (
    <Flex direction="column" gap={3}>
      <Flex align="center" justify="space-between" gap={3} wrap="wrap">
        <Flex align="center" gap={2}>
          <Heading size="xs" textTransform="uppercase" letterSpacing="wide">
            {t('inquiry.tierPrices')}
          </Heading>
          {masterInquiry.mpqQuantity != null && (
            <Badge colorScheme="purple" fontSize="xs">
              {t('inquiry.mpq')}: {masterInquiry.mpqQuantity}
            </Badge>
          )}
        </Flex>
        <WarningComponent
          warning={
            masterInquiry.tierPrices.length === 0
              ? t('inquiry.addTierWarning')
              : undefined
          }
          isMandatory={true}
        >
          <Button
            size="sm"
            variant="entityDirectorySecondary"
            leftIcon={<Icons.Plus boxSize={4} />}
            onClick={tierDrawer.onOpen}
          >
            {t('inquiry.addMoreTiers')}
          </Button>
        </WarningComponent>
      </Flex>
      <Flex align="center" gap={3} wrap="wrap">
        {toolbarControls}
        <FormControl display="flex" alignItems="center" gap={2} w="auto">
          <Switch
            id="show-with-fees"
            size="sm"
            isChecked={showWithFees}
            onChange={(e) => setShowWithFees(e.target.checked)}
          />
          <FormLabel
            htmlFor="show-with-fees"
            mb={0}
            fontSize="sm"
            fontWeight="normal"
            cursor="pointer"
          >
            {t('inquiry.showWithFees')}
          </FormLabel>
        </FormControl>
        <FormControl display="flex" alignItems="center" gap={2} w="auto">
          <Switch
            id="compare-previous-price"
            size="sm"
            isChecked={comparePreviousPrice}
            onChange={(e) => setComparePreviousPrice(e.target.checked)}
          />
          <FormLabel
            htmlFor="compare-previous-price"
            mb={0}
            fontSize="sm"
            fontWeight="normal"
            cursor="pointer"
          >
            {t('general.comparePreviousPrice')}
          </FormLabel>
        </FormControl>
      </Flex>
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th w="24px" px={1} />
              {!hideIdentityColumns && <Th>{t('inquiry.masterNumber')}</Th>}
              {!hideIdentityColumns && <Th>{t('suppliers.supplier')}</Th>}
              {!hideIdentityColumns && <Th>{t('general.branch')}</Th>}
              {hasAnyX && <Th w={12}>{t('general.cellX')}</Th>}
              {hasAnyY && <Th w={12}>{t('general.cellY')}</Th>}
              {!hideIdentityColumns && <Th>{t('general.version')}</Th>}
              {!hideIdentityColumns && <Th>{t('general.option')}</Th>}
              {!hideIdentityColumns && (
                <Th>{t('compositionGroup.hover.mainProductApproval')}</Th>
              )}
              {!hideIdentityColumns && <Th>{t('inquiry.mpq')}</Th>}
              {allTierColumns.map((qty) => {
                const anyBelowMpq = inquiries.some(
                  (inq) => inq.mpqQuantity != null && qty < inq.mpqQuantity,
                );
                const isTargetTier = targetTierQuantities.has(qty);
                const isMainTier = mainTierQuantities.has(qty);
                const masterTier = masterInquiry.tierPrices.find(
                  (tier) => tier.quantity === qty,
                );
                return (
                  <Th
                    key={qty}
                    cursor="pointer"
                    opacity={anyBelowMpq ? 0.7 : 1}
                    onClick={() => toggleColumn(qty)}
                    maxW="110px"
                    w="110px"
                    bg={
                      isMainTier
                        ? 'inquiryOverview.activeBadgeBackground'
                        : undefined
                    }
                    color={
                      isMainTier ? 'inquiryOverview.activeBadgeText' : undefined
                    }
                  >
                    <Flex justify="flex-start" align="center" gap={1}>
                      <Tooltip label={t('inquiry.selectColumn')}>
                        <IconButton
                          aria-label={t('inquiry.selectColumn')}
                          icon={<Icons.CheckAll boxSize={3} />}
                          size="xs"
                          variant="ghost"
                          minW={5}
                          h={5}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            toggleColumn(qty);
                          }}
                        />
                      </Tooltip>
                      <Tooltip
                        label={
                          isMainTier
                            ? t('inquiry.mainTier')
                            : t('inquiry.setAsMainTier')
                        }
                      >
                        <span>
                          <IconButton
                            aria-label={
                              isMainTier
                                ? t('inquiry.mainTier')
                                : t('inquiry.setAsMainTier')
                            }
                            icon={<Icons.Flag boxSize={3} />}
                            size="xs"
                            variant="ghost"
                            color={
                              isMainTier
                                ? 'inquiryOverview.activeBadgeText'
                                : undefined
                            }
                            _disabled={{ opacity: 1 }}
                            minW={5}
                            h={5}
                            isDisabled={
                              masterTier == null ||
                              isMainTier ||
                              settingMainTier
                            }
                            isLoading={settingMainTier}
                            onClick={(event: React.MouseEvent) => {
                              event.stopPropagation();
                              if (masterTier) setMainTier(masterTier.id);
                            }}
                          />
                        </span>
                      </Tooltip>
                      {isTargetTier && (
                        <Tooltip label={t('inquiry.isTargetTier')}>
                          <span>
                            <Icons.Target boxSize={3} color="blue.300" />
                          </span>
                        </Tooltip>
                      )}
                      {qty}
                    </Flex>
                  </Th>
                );
              })}
              {rowActions && <Th />}
            </Tr>
          </Thead>
          <Tbody>
            {inquiries.map((inq) => {
              const currencyCode = findCurrency(inq.currencyId)?.code ?? '';

              return (
                <Tr
                  sx={{
                    '> td': { verticalAlign: 'top !important' },
                  }}
                  key={inq.id}
                  cursor="default"
                  transition="background 150ms ease"
                  onClick={() => toggleRow(inq)}
                >
                  <Td w="24px" px={1}>
                    {inq.acceptedDate ? (
                      <Tooltip label={t('inquiry.accepted')}>
                        <span>
                          <Icons.Checkmark color="green.400" />
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip label={t('inquiry.selectRow')}>
                        <IconButton
                          aria-label={t('inquiry.selectRow')}
                          icon={<Icons.CheckAll boxSize={3} />}
                          size="xs"
                          variant="ghost"
                          minW={5}
                          h={5}
                          isDisabled={inq.tierPrices.length === 0}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            toggleRow(inq);
                          }}
                        />
                      </Tooltip>
                    )}
                  </Td>
                  {!hideIdentityColumns && (
                    <Td>
                      {inq.controllerFormattedInquiryNumber ? (
                        <CopyValue
                          value={inq.controllerFormattedInquiryNumber}
                          fontSize="xs"
                          color="gray.500"
                        />
                      ) : null}
                    </Td>
                  )}
                  {!hideIdentityColumns && (
                    <Td>
                      <Text fontSize="sm">{inq.supplierName}</Text>
                    </Td>
                  )}
                  {!hideIdentityColumns && (
                    <Td>
                      <Text fontSize="xs" color="gray.400">
                        {inq.branchIdentifier}
                      </Text>
                    </Td>
                  )}
                  {hasAnyX && (
                    <Td>
                      <Text fontSize="xs">{inq.matrixNodeXValue}</Text>
                    </Td>
                  )}
                  {hasAnyY && (
                    <Td>
                      <Text fontSize="xs">{inq.matrixNodeYValue}</Text>
                    </Td>
                  )}
                  {!hideIdentityColumns && (
                    <Td>
                      <CompositionGroupTitleWithHover
                        compositionGroupId={inq.compositionGroupId}
                        version={inq.version}
                        option={inq.basedOnGroupVersion}
                        note={null}
                        type="version"
                        fontSize="xs"
                      />
                    </Td>
                  )}
                  {!hideIdentityColumns && (
                    <Td>
                      {inq.basedOnGroupVersion && (
                        <CompositionGroupTitle
                          version={inq.basedOnGroupVersion}
                          note={inq.basedOnGroupName}
                          type="option"
                          fontSize="xs"
                        />
                      )}
                    </Td>
                  )}
                  {!hideIdentityColumns && (
                    <Td>
                      <Text fontSize="xs">
                        {inq.mainProductApprovalNumber ?? '—'}
                      </Text>
                    </Td>
                  )}
                  {!hideIdentityColumns && (
                    <Td>
                      <Text fontSize="xs">{inq.mpqQuantity ?? '—'}</Text>
                    </Td>
                  )}
                  {allTierColumns.map((qty) => {
                    const tp = getDisplayTierPrice(inq, qty);
                    const isTargetOnly =
                      tp == null &&
                      inq.allTargetTiers.some((t) => t.quantity === qty);
                    const isBelowMpq =
                      inq.mpqQuantity != null && qty < inq.mpqQuantity;
                    const isSelected =
                      tp != null && selectedTierPriceIds.has(tp.id);
                    const isTierRejected = tp?.rejectedAt != null;
                    const isTierAccepted =
                      tp?.acceptedDate != null && !isTierRejected;
                    return (
                      <Td
                        key={qty}
                        opacity={isBelowMpq ? 0.7 : isTargetOnly ? 0.7 : 1}
                        {...getTierCellBackgroundColor({
                          isSelected,
                          isTierAccepted,
                          isTierRejected,
                        })}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        maxW="160px"
                        w="160px"
                      >
                        {tp ? (
                          <TierCell
                            key={tp.id}
                            tp={tp}
                            inq={inq}
                            currencyCode={currencyCode}
                            showWithFees={showWithFees}
                            comparePreviousPrice={comparePreviousPrice}
                            isSelected={isSelected}
                            onToggleCell={toggleCell}
                            onCreateVersion={createSingleVersion}
                            onAccept={acceptSingleTierPrice}
                            onReject={rejectSingleTierPrice}
                            onReview={reviewSingleTierPrice}
                            onOpenHistory={(tierPriceId, code) =>
                              openHistory(tierPriceId, code, inq)
                            }
                          />
                        ) : isTargetOnly ? (
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            opacity={0.8}
                            userSelect="none"
                          >
                            —
                          </Text>
                        ) : null}
                      </Td>
                    );
                  })}
                  {rowActions && (
                    <Td onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                      {rowActions(inq)}
                    </Td>
                  )}
                </Tr>
              );
            })}
            {missingCellSlots.map((slot) => (
              <Tr
                key={`missing-${slot.compositionGroupPurchaseInfoId}`}
                opacity={0.4}
                __css={{ verticalAlign: 'top !important' }}
              >
                <Td />
                {!hideIdentityColumns && <Td />}
                {!hideIdentityColumns && (
                  <Td>
                    <Text fontSize="sm">{slot.supplierName}</Text>
                  </Td>
                )}
                {!hideIdentityColumns && (
                  <Td>
                    <Text fontSize="xs" color="gray.400">
                      {slot.branchIdentifier}
                    </Text>
                  </Td>
                )}
                {hasAnyX && (
                  <Td>
                    <Text fontSize="xs">{slot.matrixNodeXValue}</Text>
                  </Td>
                )}
                {hasAnyY && (
                  <Td>
                    <Text fontSize="xs">{slot.matrixNodeYValue}</Text>
                  </Td>
                )}
                {!hideIdentityColumns && (
                  <Td>
                    <CompositionGroupTitleWithHover
                      compositionGroupId={slot.compositionGroupId}
                      version={slot.version}
                      option={slot.basedOnGroupVersion}
                      note={null}
                      type="version"
                      fontSize="xs"
                    />
                  </Td>
                )}
                {!hideIdentityColumns && (
                  <Td>
                    {slot.basedOnGroupVersion && (
                      <CompositionGroupTitle
                        version={slot.basedOnGroupVersion}
                        note={slot.basedOnGroupName}
                        type="option"
                        fontSize="xs"
                      />
                    )}
                  </Td>
                )}
                {!hideIdentityColumns && (
                  <Td>
                    <Text fontSize="xs">
                      {slot.mainProductApprovalNumber ?? '—'}
                    </Text>
                  </Td>
                )}
                {!hideIdentityColumns && <Td />}
                {allTierColumns.map((qty) => (
                  <Td key={qty} />
                ))}
                {rowActions && <Td />}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Flex justify="space-between" align="center" gap={3} wrap="wrap">
        <Checkbox
          size="sm"
          isChecked={
            selectableTierPriceIds.length > 0 &&
            selectedTierPriceIds.size === selectableTierPriceIds.length
          }
          isIndeterminate={
            selectedTierPriceIds.size > 0 &&
            selectedTierPriceIds.size < selectableTierPriceIds.length
          }
          isDisabled={selectableTierPriceIds.length === 0}
          onChange={() =>
            selectedTierPriceIds.size === selectableTierPriceIds.length
              ? clearSelection()
              : selectAllPrices()
          }
        >
          {t('general.selectAll')}
        </Checkbox>
        <Flex align="center" gap={2} wrap="wrap">
          <Text color="inquiryOverview.mutedText" fontSize="sm">
            {t('inquiry.selectedTierCount', {
              selected: selectedTierPriceIds.size,
              total: selectableTierPriceIds.length,
            })}
          </Text>
          {selectedTierPriceIds.size > 0 && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Icons.Close boxSize={3} />}
              onClick={clearSelection}
            >
              {t('general.deselectAll')}
            </Button>
          )}
          {selectedTierPriceIds.size > 0 && (
            <>
              <Button
                size="sm"
                variant="texas-solid"
                isDisabled={selectedEditableTierPriceIds.length === 0}
                onClick={() => setModalOpen(true)}
              >
                {t('inquiry.setTierPrice')}
              </Button>
              <Button
                size="sm"
                variant="texas-solid"
                isDisabled={selectedAcceptableTierPriceIds.length === 0}
                onClick={acceptDialog.onOpen}
              >
                {t('inquiry.acceptSelectedPrices')}
              </Button>
              <Button
                size="sm"
                variant="texas-solid"
                onClick={rejectDialog.onOpen}
              >
                {t('inquiry.rejectSelectedPrices')}
              </Button>
              <Button
                size="sm"
                variant="texas-solid"
                isDisabled={selectedReviewedTierPriceIds.length === 0}
                onClick={reviewDialog.onOpen}
              >
                {t('inquiry.setUnderReview')}
              </Button>
              <Button
                size="sm"
                variant="texas-solid"
                isDisabled={selectedLockedTierPriceIds.length === 0}
                onClick={newVersionDialog.onOpen}
              >
                {t('inquiry.createNewVersion')}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
      {allTierColumns.some((qty) =>
        inquiries.some(
          (inq) => inq.mpqQuantity != null && qty < inq.mpqQuantity,
        ),
      ) && (
        <Text fontSize="xs" color="orange.300" mt={1}>
          * {t('inquiry.belowMpq')}
        </Text>
      )}
      {mainTier &&
        masterInquiry.mpqQuantity != null &&
        mainTier.quantity < masterInquiry.mpqQuantity && (
          <Text fontSize="xs" color="orange.300">
            {t('inquiry.mainTierBelowMpq')}
          </Text>
        )}
      {modalOpen && (
        <SetTierPriceModal
          selectedCells={buildSelectedCells()}
          isOpen={true}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            inquiryEvents.onUpdated.dispatch();
            onChanged?.();
            setModalOpen(false);
            clearSelection();
          }}
        />
      )}
      <VerifyDialogWithRequest
        headerTitle={t('inquiry.acceptSelectedPrices')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('inquiry.accept')}
        primaryRequest={inquiryApi.acceptTierPrices}
        args={[{ tierPriceIds: selectedAcceptableTierPriceIds }]}
        isOpen={acceptDialog.isOpen}
        onClose={acceptDialog.onClose}
        onPerformed={() => {
          inquiryEvents.onUpdated.dispatch();
          onChanged?.();
          clearSelection();
        }}
        onSuccessTitle={t('inquiry.acceptedPrices')}
      >
        {t('inquiry.acceptPricesConfirm', {
          count: selectedAcceptableTierPriceIds.length,
        })}
      </VerifyDialogWithRequest>
      <VerifyDialogWithRequest
        headerTitle={t('inquiry.rejectSelectedPrices')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('inquiry.reject')}
        primaryRequest={inquiryApi.rejectTierPrices}
        args={[{ tierPriceIds: Array.from(selectedTierPriceIds) }]}
        isOpen={rejectDialog.isOpen}
        onClose={rejectDialog.onClose}
        onPerformed={() => {
          inquiryEvents.onUpdated.dispatch();
          onChanged?.();
          clearSelection();
        }}
        onSuccessTitle={t('inquiry.rejectedPrices')}
      >
        {t('inquiry.rejectPricesConfirm', {
          count: selectedTierPriceIds.size,
        })}
      </VerifyDialogWithRequest>
      <VerifyDialogWithRequest
        headerTitle={t('inquiry.setUnderReview')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={inquiryApi.reviewTierPrices}
        args={[{ tierPriceIds: selectedReviewedTierPriceIds }]}
        isOpen={reviewDialog.isOpen}
        onClose={reviewDialog.onClose}
        onPerformed={() => {
          inquiryEvents.onUpdated.dispatch();
          onChanged?.();
          clearSelection();
        }}
        onSuccessTitle={t('general.saved')}
      >
        {t('inquiry.setUnderReviewConfirm', {
          count: selectedReviewedTierPriceIds.length,
        })}
      </VerifyDialogWithRequest>
      <VerifyDialogWithRequest
        headerTitle={t('inquiry.createNewVersion')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={inquiryApi.createTierPriceVersions}
        args={[{ tierPriceIds: selectedLockedTierPriceIds }]}
        isOpen={newVersionDialog.isOpen}
        onClose={newVersionDialog.onClose}
        onPerformed={() => {
          inquiryEvents.onUpdated.dispatch();
          onChanged?.();
          clearSelection();
        }}
        onSuccessTitle={t('general.created')}
      >
        {t('inquiry.createNewVersionConfirm', {
          count: selectedLockedTierPriceIds.length,
        })}
      </VerifyDialogWithRequest>
      <InquiryTierPriceHistoryModal
        tierPriceId={historyDialog.value?.tierPriceId ?? null}
        currencyCode={historyDialog.value?.currencyCode ?? ''}
        title={historyDialog.value?.title ?? null}
        isOpen={historyDialog.isOpen}
        onClose={historyDialog.onClose}
      />
      <TiersEditDrawer
        inquiry={masterInquiry}
        isOpen={tierDrawer.isOpen}
        onClose={tierDrawer.onClose}
      />
    </Flex>
  );
}

function getInquiryHistoryTitle(inquiry: InquiryWithContext) {
  const cell = [inquiry.matrixNodeXValue, inquiry.matrixNodeYValue]
    .filter(Boolean)
    .join(' / ');
  return [inquiry.formattedInquiryNumber, inquiry.branchIdentifier, cell]
    .filter(Boolean)
    .join(' - ');
}

function getTierCellBackgroundColor(props: {
  isSelected: boolean;
  isTierAccepted: boolean;
  isTierRejected: boolean;
}) {
  return {
    bg: props.isSelected
      ? 'blue.800'
      : props.isTierAccepted
      ? 'green.900'
      : props.isTierRejected
      ? 'red.900'
      : undefined,
    _light: {
      bg: props.isSelected
        ? 'blue.100'
        : props.isTierAccepted
        ? 'green.50'
        : props.isTierRejected
        ? 'red.50'
        : undefined,
    },
  };
}
